import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
} from "recharts";
import {
  DollarSign,
  TrendingUp,
  Target,
  Users,
  Plus,
  Upload,
  Download,
  RefreshCw,
  BarChart3,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import AdSpendCsvImport from "./AdSpendCsvImport";

const PLATFORMS = ["meta", "google", "tiktok", "linkedin", "twitter", "other"] as const;

const PLATFORM_LABELS: Record<string, string> = {
  meta: "Meta (Facebook/Instagram)",
  google: "Google Ads",
  tiktok: "TikTok Ads",
  linkedin: "LinkedIn Ads",
  twitter: "X (Twitter) Ads",
  other: "Other",
};

const PLATFORM_COLORS: Record<string, string> = {
  meta: "hsl(220, 80%, 55%)",
  google: "hsl(45, 90%, 50%)",
  tiktok: "hsl(340, 80%, 55%)",
  linkedin: "hsl(210, 70%, 45%)",
  twitter: "hsl(200, 80%, 50%)",
  other: "hsl(0, 0%, 60%)",
};

interface AdSpendRow {
  id: string;
  platform: string;
  campaign_name: string | null;
  campaign_id: string | null;
  ad_set_name: string | null;
  ad_set_id: string | null;
  ad_name: string | null;
  ad_id: string | null;
  spend_date: string;
  spend_cents: number;
  impressions: number;
  clicks: number;
}

interface UtmAttribution {
  id: string;
  order_id: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  landing_page: string | null;
  referrer: string | null;
  created_at: string;
}

interface OrderRow {
  id: string;
  amount_cents: number;
  patient_id: string | null;
  utm_source: string | null;
  created_at: string;
  status: string;
}

const CacLtvDashboard = () => {
  const { toast } = useToast();
  const [subTab, setSubTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [adSpend, setAdSpend] = useState<AdSpendRow[]>([]);
  const [attributions, setAttributions] = useState<UtmAttribution[]>([]);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = subMonths(end, 3);
    return { start: format(startOfMonth(start), "yyyy-MM-dd"), end: format(endOfMonth(end), "yyyy-MM-dd") };
  });

  // Add spend form
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    platform: "meta",
    campaign_name: "",
    campaign_id: "",
    ad_set_name: "",
    ad_set_id: "",
    ad_name: "",
    ad_id: "",
    spend_date: format(new Date(), "yyyy-MM-dd"),
    spend_dollars: "",
    impressions: "",
    clicks: "",
  });

  const fetchData = async () => {
    setLoading(true);
    const [spendRes, attrRes, ordersRes] = await Promise.all([
      supabase
        .from("ad_spend")
        .select("*")
        .gte("spend_date", dateRange.start)
        .lte("spend_date", dateRange.end)
        .order("spend_date", { ascending: false }),
      supabase
        .from("utm_attribution")
        .select("*")
        .gte("created_at", dateRange.start)
        .lte("created_at", dateRange.end + "T23:59:59"),
      supabase
        .from("orders")
        .select("id, amount_cents, patient_id, utm_source, created_at, status")
        .gte("created_at", dateRange.start)
        .lte("created_at", dateRange.end + "T23:59:59")
        .eq("status", "active"),
    ]);

    setAdSpend((spendRes.data as AdSpendRow[]) || []);
    setAttributions((attrRes.data as UtmAttribution[]) || []);
    setOrders((ordersRes.data as OrderRow[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  // --- Metrics ---
  const metrics = useMemo(() => {
    const totalSpend = adSpend.reduce((s, r) => s + r.spend_cents, 0);
    const totalRevenue = orders.reduce((s, o) => s + o.amount_cents, 0);
    const paidConversions = orders.filter((o) => o.utm_source).length;
    const totalConversions = orders.length;
    const cac = paidConversions > 0 ? totalSpend / paidConversions : 0;
    const avgOrderValue = totalConversions > 0 ? totalRevenue / totalConversions : 0;

    // Simple LTV: avg order value × estimated repeat (count orders per unique patient)
    const patientOrders = new Map<string, number>();
    orders.forEach((o) => {
      if (o.patient_id) {
        patientOrders.set(o.patient_id, (patientOrders.get(o.patient_id) || 0) + 1);
      }
    });
    const avgRepeat = patientOrders.size > 0
      ? Array.from(patientOrders.values()).reduce((a, b) => a + b, 0) / patientOrders.size
      : 1;
    const ltv = avgOrderValue * avgRepeat;
    const roas = totalSpend > 0 ? totalRevenue / totalSpend : 0;

    return { totalSpend, totalRevenue, paidConversions, totalConversions, cac, avgOrderValue, ltv, roas, uniquePatients: patientOrders.size };
  }, [adSpend, orders]);

  // --- Per-platform breakdown ---
  const platformBreakdown = useMemo(() => {
    const spendByPlatform = new Map<string, number>();
    adSpend.forEach((r) => {
      spendByPlatform.set(r.platform, (spendByPlatform.get(r.platform) || 0) + r.spend_cents);
    });

    const convByPlatform = new Map<string, { count: number; revenue: number }>();
    orders.forEach((o) => {
      const src = o.utm_source?.toLowerCase() || "organic";
      // Map utm_source to platform
      let platform = "organic";
      if (src.includes("facebook") || src.includes("instagram") || src.includes("meta") || src === "fb" || src === "ig") platform = "meta";
      else if (src.includes("google") || src === "gclid") platform = "google";
      else if (src.includes("tiktok")) platform = "tiktok";
      else if (src.includes("linkedin")) platform = "linkedin";
      else if (src.includes("twitter") || src.includes("x.com")) platform = "twitter";
      else if (src !== "organic") platform = "other";

      const existing = convByPlatform.get(platform) || { count: 0, revenue: 0 };
      existing.count++;
      existing.revenue += o.amount_cents;
      convByPlatform.set(platform, existing);
    });

    const allPlatforms = new Set([...spendByPlatform.keys(), ...convByPlatform.keys()]);
    return Array.from(allPlatforms)
      .filter((p) => p !== "organic")
      .map((platform) => {
        const spend = spendByPlatform.get(platform) || 0;
        const conv = convByPlatform.get(platform) || { count: 0, revenue: 0 };
        const cac = conv.count > 0 ? spend / conv.count : 0;
        const roas = spend > 0 ? conv.revenue / spend : 0;
        return {
          platform,
          label: PLATFORM_LABELS[platform] || platform,
          spend,
          conversions: conv.count,
          revenue: conv.revenue,
          cac,
          roas,
        };
      })
      .sort((a, b) => b.spend - a.spend);
  }, [adSpend, orders]);

  // --- Campaign-level breakdown ---
  const campaignBreakdown = useMemo(() => {
    const byCampaign = new Map<string, { spend: number; impressions: number; clicks: number; platform: string }>();
    adSpend.forEach((r) => {
      const key = r.campaign_name || r.campaign_id || "Unknown";
      const existing = byCampaign.get(key) || { spend: 0, impressions: 0, clicks: 0, platform: r.platform };
      existing.spend += r.spend_cents;
      existing.impressions += r.impressions;
      existing.clicks += r.clicks;
      byCampaign.set(key, existing);
    });

    const convByCampaign = new Map<string, { count: number; revenue: number }>();
    attributions.forEach((a) => {
      const campName = a.utm_campaign || "Unknown";
      const order = orders.find((o) => o.id === a.order_id);
      if (order) {
        const existing = convByCampaign.get(campName) || { count: 0, revenue: 0 };
        existing.count++;
        existing.revenue += order.amount_cents;
        convByCampaign.set(campName, existing);
      }
    });

    return Array.from(byCampaign.entries()).map(([name, data]) => {
      const conv = convByCampaign.get(name) || { count: 0, revenue: 0 };
      return {
        campaign: name,
        platform: data.platform,
        spend: data.spend,
        impressions: data.impressions,
        clicks: data.clicks,
        conversions: conv.count,
        revenue: conv.revenue,
        cac: conv.count > 0 ? data.spend / conv.count : 0,
        roas: data.spend > 0 ? conv.revenue / data.spend : 0,
        ctr: data.impressions > 0 ? (data.clicks / data.impressions) * 100 : 0,
      };
    }).sort((a, b) => b.spend - a.spend);
  }, [adSpend, attributions, orders]);

  // --- Chart data ---
  const chartData = useMemo(() => {
    return platformBreakdown.map((p) => ({
      name: p.label.split(" ")[0],
      "Spend ($)": Math.round(p.spend) / 100,
      "Revenue ($)": Math.round(p.revenue) / 100,
      "CAC ($)": Math.round(p.cac) / 100,
    }));
  }, [platformBreakdown]);

  const handleAddSpend = async () => {
    const spendCents = Math.round(parseFloat(addForm.spend_dollars || "0") * 100);
    if (spendCents <= 0) {
      toast({ title: "Invalid spend", description: "Enter a valid spend amount.", variant: "destructive" });
      return;
    }

    const { error } = await supabase.from("ad_spend").insert({
      platform: addForm.platform,
      campaign_name: addForm.campaign_name || null,
      campaign_id: addForm.campaign_id || null,
      ad_set_name: addForm.ad_set_name || null,
      ad_set_id: addForm.ad_set_id || null,
      ad_name: addForm.ad_name || null,
      ad_id: addForm.ad_id || null,
      spend_date: addForm.spend_date,
      spend_cents: spendCents,
      impressions: parseInt(addForm.impressions || "0") || 0,
      clicks: parseInt(addForm.clicks || "0") || 0,
    });

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Ad spend added" });
      setAddOpen(false);
      setAddForm({
        platform: "meta",
        campaign_name: "",
        campaign_id: "",
        ad_set_name: "",
        ad_set_id: "",
        ad_name: "",
        ad_id: "",
        spend_date: format(new Date(), "yyyy-MM-dd"),
        spend_dollars: "",
        impressions: "",
        clicks: "",
      });
      fetchData();
    }
  };

  const handleExportCsv = () => {
    const rows = [
      ["Platform", "Campaign", "Ad Set", "Ad", "Date", "Spend ($)", "Impressions", "Clicks"],
      ...adSpend.map((r) => [
        r.platform,
        r.campaign_name || "",
        r.ad_set_name || "",
        r.ad_name || "",
        r.spend_date,
        (r.spend_cents / 100).toFixed(2),
        r.impressions.toString(),
        r.clicks.toString(),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ad-spend-${dateRange.start}-to-${dateRange.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  return (
    <div className="space-y-6">
      {/* Date range & actions */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs text-muted-foreground">From</Label>
          <Input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange((p) => ({ ...p, start: e.target.value }))}
            className="w-40"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">To</Label>
          <Input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange((p) => ({ ...p, end: e.target.value }))}
            className="w-40"
          />
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus size={14} className="mr-1" /> Add Spend
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setImportOpen(true)}>
          <Upload size={14} className="mr-1" /> Import CSV
        </Button>
        <Button variant="outline" size="sm" onClick={handleExportCsv}>
          <Download size={14} className="mr-1" /> Export CSV
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <DollarSign size={14} /> Total Ad Spend
            </div>
            <p className="text-2xl font-bold">{fmt(metrics.totalSpend)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Target size={14} /> CAC (Paid)
            </div>
            <p className="text-2xl font-bold">{metrics.paidConversions > 0 ? fmt(metrics.cac) : "—"}</p>
            <p className="text-xs text-muted-foreground">{metrics.paidConversions} paid conversions</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Users size={14} /> Est. LTV
            </div>
            <p className="text-2xl font-bold">{fmt(metrics.ltv)}</p>
            <p className="text-xs text-muted-foreground">{metrics.uniquePatients} unique patients</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <TrendingUp size={14} /> ROAS
            </div>
            <p className="text-2xl font-bold">{metrics.roas > 0 ? `${metrics.roas.toFixed(2)}x` : "—"}</p>
            <p className="text-xs text-muted-foreground">{fmt(metrics.totalRevenue)} revenue</p>
          </CardContent>
        </Card>
      </div>

      {/* Sub-tabs */}
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="flex overflow-x-auto no-scrollbar h-auto gap-1 p-1 w-full justify-start">
          <TabsTrigger value="overview" className="shrink-0 min-h-11">Platform Overview</TabsTrigger>
          <TabsTrigger value="campaigns" className="shrink-0 min-h-11">Campaigns</TabsTrigger>
          <TabsTrigger value="spend" className="shrink-0 min-h-11">Ad Spend Log</TabsTrigger>
          <TabsTrigger value="attributions" className="shrink-0 min-h-11">Attributions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Chart */}
          {chartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <BarChart3 size={16} /> Spend vs Revenue by Platform
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(v: number) => `$${v.toFixed(2)}`} />
                    <Legend />
                    <Bar dataKey="Spend ($)" fill="hsl(var(--muted-foreground))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Revenue ($)" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Platform table */}
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Platform</TableHead>
                    <TableHead className="text-right">Spend</TableHead>
                    <TableHead className="text-right">Conversions</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">CAC</TableHead>
                    <TableHead className="text-right">ROAS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {platformBreakdown.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No ad spend data yet. Add spend entries to see platform analytics.
                      </TableCell>
                    </TableRow>
                  )}
                  {platformBreakdown.map((p) => (
                    <TableRow key={p.platform}>
                      <TableCell>
                        <Badge variant="outline">{p.label}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{fmt(p.spend)}</TableCell>
                      <TableCell className="text-right">{p.conversions}</TableCell>
                      <TableCell className="text-right">{fmt(p.revenue)}</TableCell>
                      <TableCell className="text-right font-medium">
                        {p.conversions > 0 ? fmt(p.cac) : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {p.roas > 0 ? `${p.roas.toFixed(2)}x` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="campaigns" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead className="text-right">Spend</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">CTR</TableHead>
                    <TableHead className="text-right">Conv.</TableHead>
                    <TableHead className="text-right">CAC</TableHead>
                    <TableHead className="text-right">ROAS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaignBreakdown.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                        No campaign data available.
                      </TableCell>
                    </TableRow>
                  )}
                  {campaignBreakdown.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell className="max-w-[200px] truncate">{c.campaign}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{PLATFORM_LABELS[c.platform]?.split(" ")[0] || c.platform}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{fmt(c.spend)}</TableCell>
                      <TableCell className="text-right">{c.clicks.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{c.ctr.toFixed(2)}%</TableCell>
                      <TableCell className="text-right">{c.conversions}</TableCell>
                      <TableCell className="text-right">{c.conversions > 0 ? fmt(c.cac) : "—"}</TableCell>
                      <TableCell className="text-right">{c.roas > 0 ? `${c.roas.toFixed(2)}x` : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="spend" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Ad Set</TableHead>
                    <TableHead className="text-right">Spend</TableHead>
                    <TableHead className="text-right">Impr.</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adSpend.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No ad spend entries. Click "Add Spend" to get started.
                      </TableCell>
                    </TableRow>
                  )}
                  {adSpend.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.spend_date}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{PLATFORM_LABELS[r.platform]?.split(" ")[0] || r.platform}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">{r.campaign_name || "—"}</TableCell>
                      <TableCell className="max-w-[150px] truncate">{r.ad_set_name || "—"}</TableCell>
                      <TableCell className="text-right">{fmt(r.spend_cents)}</TableCell>
                      <TableCell className="text-right">{r.impressions.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{r.clicks.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attributions" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Medium</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Content</TableHead>
                    <TableHead>Term</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attributions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No UTM attributions recorded yet. Conversions from ad campaigns will appear here.
                      </TableCell>
                    </TableRow>
                  )}
                  {attributions.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{format(new Date(a.created_at), "MMM d, yyyy")}</TableCell>
                      <TableCell>
                        {a.utm_source ? <Badge variant="outline">{a.utm_source}</Badge> : "—"}
                      </TableCell>
                      <TableCell>{a.utm_medium || "—"}</TableCell>
                      <TableCell className="max-w-[150px] truncate">{a.utm_campaign || "—"}</TableCell>
                      <TableCell className="max-w-[120px] truncate">{a.utm_content || "—"}</TableCell>
                      <TableCell>{a.utm_term || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Spend Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Ad Spend Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Platform</Label>
              <Select value={addForm.platform} onValueChange={(v) => setAddForm((p) => ({ ...p, platform: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => (
                    <SelectItem key={p} value={p}>{PLATFORM_LABELS[p]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Campaign Name</Label>
                <Input value={addForm.campaign_name} onChange={(e) => setAddForm((p) => ({ ...p, campaign_name: e.target.value }))} />
              </div>
              <div>
                <Label>Campaign ID</Label>
                <Input value={addForm.campaign_id} onChange={(e) => setAddForm((p) => ({ ...p, campaign_id: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Ad Set Name</Label>
                <Input value={addForm.ad_set_name} onChange={(e) => setAddForm((p) => ({ ...p, ad_set_name: e.target.value }))} />
              </div>
              <div>
                <Label>Ad Set ID</Label>
                <Input value={addForm.ad_set_id} onChange={(e) => setAddForm((p) => ({ ...p, ad_set_id: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Ad Name</Label>
                <Input value={addForm.ad_name} onChange={(e) => setAddForm((p) => ({ ...p, ad_name: e.target.value }))} />
              </div>
              <div>
                <Label>Ad ID</Label>
                <Input value={addForm.ad_id} onChange={(e) => setAddForm((p) => ({ ...p, ad_id: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Spend Date</Label>
              <Input type="date" value={addForm.spend_date} onChange={(e) => setAddForm((p) => ({ ...p, spend_date: e.target.value }))} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Spend ($)</Label>
                <Input type="number" step="0.01" value={addForm.spend_dollars} onChange={(e) => setAddForm((p) => ({ ...p, spend_dollars: e.target.value }))} placeholder="0.00" />
              </div>
              <div>
                <Label>Impressions</Label>
                <Input type="number" value={addForm.impressions} onChange={(e) => setAddForm((p) => ({ ...p, impressions: e.target.value }))} placeholder="0" />
              </div>
              <div>
                <Label>Clicks</Label>
                <Input type="number" value={addForm.clicks} onChange={(e) => setAddForm((p) => ({ ...p, clicks: e.target.value }))} placeholder="0" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAddSpend}>Add Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdSpendCsvImport open={importOpen} onOpenChange={setImportOpen} onImported={fetchData} />
    </div>
  );
};

export default CacLtvDashboard;
